import CloseIcon from "@mui/icons-material/Close";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Box, IconButton, Typography } from "@mui/material";
import React, { RefObject } from "react";
import PlayerContent from "./PlayerContent";
import PlayerDelete from "./PlayerDelete";
import PlayerError from "./PlayerError";

interface PlayerVideoContentProps {
  videoRef: RefObject<HTMLVideoElement>;
  showError: boolean;
  stalled: boolean;
  onDelete: () => void;
  canDelete: boolean;
  onRetry: () => void;
  cameraName?: string;
}

const PlayerVideoContent: React.FC<PlayerVideoContentProps> = ({
  videoRef,
  showError,
  stalled,
  onDelete,
  canDelete,
  onRetry,
  cameraName,
}) => {
  if (showError) {
    return (
      <PlayerError>
        <VideocamOffIcon sx={{ fontSize: 32, color: "rgba(255,255,255,0.2)" }} />
        <Typography
          sx={{
            fontSize: "0.8rem",
            color: "rgba(255,255,255,0.4)",
            fontWeight: 500,
          }}
        >
          {cameraName ? `${cameraName} — offline` : "Camera offline"}
        </Typography>
        <IconButton
          onClick={onRetry}
          size="small"
          sx={{
            color: "rgba(255,255,255,0.3)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 1,
            px: 1.5,
            py: 0.5,
            fontSize: "0.75rem",
            gap: 0.5,
            "&:hover": {
              color: "primary.main",
              borderColor: "rgba(79,195,247,0.3)",
              backgroundColor: "rgba(79,195,247,0.05)",
            },
          }}
        >
          <RefreshIcon sx={{ fontSize: 16 }} />
          <Box component="span" sx={{ fontSize: "0.75rem" }}>
            Retry
          </Box>
        </IconButton>
      </PlayerError>
    );
  }

  return (
    <PlayerContent>
      {canDelete && (
        <PlayerDelete aria-label="remove" onClick={onDelete} className="player-delete">
          <CloseIcon />
        </PlayerDelete>
      )}
      <Box sx={{ position: "absolute", inset: 0 }}>
        <video ref={videoRef} autoPlay controls />

        {/* Stalled overlay — stream attempted but not playing.
            z-index 2 lifts us above the <video> element's implicit
            stacking layer (browsers sometimes hardware-accelerate it
            onto its own layer). A previous iteration used
            2147483647 (max int32) which punched through app chrome —
            MUI Drawer is z-index 1200, so the navigation overlay
            was being covered by this overlay. 2 is enough. */}
        {stalled && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: 1.5,
              zIndex: 2,
              background:
                "linear-gradient(135deg, rgba(13,27,42,0.9) 0%, rgba(27,40,56,0.9) 100%)",
            }}
          >
            <RefreshIcon sx={{ fontSize: 32, color: "rgba(255,255,255,0.25)" }} />
            <Typography
              sx={{
                fontSize: "0.8rem",
                color: "rgba(255,255,255,0.4)",
                fontWeight: 500,
              }}
            >
              {cameraName ? `${cameraName} — not loading` : "Stream not loading"}
            </Typography>
            <IconButton
              onClick={onRetry}
              size="small"
              sx={{
                color: "rgba(255,255,255,0.4)",
                border: "1px solid rgba(255,255,255,0.15)",
                borderRadius: 1,
                px: 2,
                py: 0.5,
                gap: 0.5,
                "&:hover": {
                  color: "primary.main",
                  borderColor: "rgba(79,195,247,0.3)",
                  backgroundColor: "rgba(79,195,247,0.05)",
                },
              }}
            >
              <RefreshIcon sx={{ fontSize: 16 }} />
              <Box component="span" sx={{ fontSize: "0.75rem" }}>
                Reload
              </Box>
            </IconButton>
          </Box>
        )}

        {/* Small reload button always available in corner */}
        {!stalled && (
          <IconButton
            onClick={onRetry}
            aria-label="reload stream"
            size="small"
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 2,
              color: "rgba(255,255,255,0.5)",
              backgroundColor: "rgba(0,0,0,0.4)",
              backdropFilter: "blur(4px)",
              "&:hover": {
                color: "#fff",
                backgroundColor: "rgba(0,0,0,0.6)",
              },
            }}
          >
            <RefreshIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </Box>
    </PlayerContent>
  );
};

export default PlayerVideoContent;
